import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Product } from '../types';
import { getWindowOpenAt, getWindowCloseAt, getWindowState, formatWindowClose } from '../utils/productWindows';

// The Clock — one quiet line under the orientation block that always answers
// "when should I come back?". Driven entirely by fragment window dates:
//   open window   → "Fragment {n} is open. Open until {date}."
//   upcoming only → "The next fragment opens {date}."
//   neither       → renders nothing (silence over filler).

type ClockState =
  | { kind: 'open'; product: Product; closeAt: string }
  | { kind: 'upcoming'; openAt: string }
  | null;

const HomeWindowStrip: React.FC = () => {
  const [state, setState] = useState<ClockState>(null);

  useEffect(() => {
    const fetchWindows = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'products'), where('tier', '==', 'fragment')));
        const now = new Date();

        let openSoonestClose: { product: Product; closeAt: string } | null = null;
        let nextOpenAt: string | null = null;

        snap.forEach((doc) => {
          const product = { id: doc.id, ...doc.data() } as Product;
          const windowState = getWindowState(product, now);

          if (windowState === 'open') {
            // Only windows that actually close belong on the clock
            const closeAt = getWindowCloseAt(product);
            if (closeAt && (!openSoonestClose || closeAt < openSoonestClose.closeAt)) {
              openSoonestClose = { product, closeAt };
            }
          } else if (windowState === 'upcoming') {
            const openAt = getWindowOpenAt(product);
            if (openAt && (!nextOpenAt || openAt < nextOpenAt)) {
              nextOpenAt = openAt;
            }
          }
        });

        if (openSoonestClose) {
          setState({ kind: 'open', ...openSoonestClose });
        } else if (nextOpenAt) {
          setState({ kind: 'upcoming', openAt: nextOpenAt });
        }
      } catch (error) {
        console.error('Error fetching window state:', error);
      }
    };

    fetchWindows();
  }, []);

  if (!state) return null;

  const href = state.kind === 'open' ? `/product/${state.product.id}` : '/shop/soon';

  return (
    <section className="bg-brand-ink">
      <Link
        to={href}
        className="block py-8 px-4 text-center font-mono text-sm md:text-base tracking-[0.2em] text-white hover:bg-brand-charcoal transition-colors"
      >
        {state.kind === 'open' ? (
          <>
            {state.product.fragmentNumber ? `Fragment ${state.product.fragmentNumber}` : state.product.title} is open.{' '}
            <span className="text-brand-gold">Open until {formatWindowClose(state.closeAt)}.</span>
          </>
        ) : (
          <>
            The next fragment opens{' '}
            <span className="text-brand-gold">
              {new Date(state.openAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
            </span>.
          </>
        )}
      </Link>
    </section>
  );
};

export default HomeWindowStrip;
